import { createContext, useContext, useState, useEffect, useMemo } from "react";

const LangContext = createContext(null);

const dict = {
  en: {
    app_name: "DNDi Zero Trust",
    app_sub: "Medical Cloud Console",
    mode_real: "Real Mode",
    mode_simulated: "Simulated Mode",
    dashboard: "Dashboard",
    catalog: "Resource Catalog",
    policies: "Policies",
    segments: "Micro-Segmentation",
    siem: "SIEM & SOC",
    scenarios: "Scenarios",
    compliance: "Compliance",
    users: "Users",
    devices: "Devices",
    sites: "Sites",
    logout: "Log out",
    login: "Sign in",
    register: "Sign up",
    email: "Email",
    password: "Password",
    name: "Full name",
    role: "Role",
    site: "Site",
    submit: "Continue",
    mfa_title: "Two-factor authentication",
    mfa_help: "Enter the 6-digit code from your authenticator app.",
    mfa_setup_title: "Set up MFA",
    mfa_setup_help: "Scan the QR code with Google Authenticator, then enter a code below.",
    verify: "Verify",
    trusted: "Trusted",
    suspicious: "Suspicious",
    blocked: "Blocked",
    allow: "Allow",
    deny: "Deny",
    mfa_plus: "MFA+",
    jit: "JIT",
    real_time_feed: "Real-time event feed",
    open_alerts: "Open alerts",
    risk_score: "Risk score",
    request_access: "Request access",
    device_posture: "Device posture",
    os_updated: "OS up to date",
    antivirus: "Antivirus active",
    disk_encryption: "Disk encryption",
    trusted_network: "Trusted network",
    logs: "Logs",
    alerts: "Alerts",
    policy_editor: "Policy editor",
    new_policy: "New policy",
    delete: "Delete",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    run: "Run",
    download_pdf: "Download PDF",
    scenarios_intro: "Replay the four validation scenarios from the memoir.",
    simulated_only: "Available only in Simulated mode",
    coverage: "Coverage",
    export_audit: "Export audit",
  },
  fr: {
    app_name: "DNDi Zero Trust",
    app_sub: "Console Cloud Médical",
    mode_real: "Mode Réel",
    mode_simulated: "Mode Simulé",
    dashboard: "Tableau de bord",
    catalog: "Catalogue de ressources",
    policies: "Politiques",
    segments: "Micro-segmentation",
    siem: "SIEM & SOC",
    scenarios: "Scénarios",
    compliance: "Conformité",
    users: "Utilisateurs",
    devices: "Terminaux",
    sites: "Sites",
    logout: "Déconnexion",
    login: "Se connecter",
    register: "S'inscrire",
    email: "Email",
    password: "Mot de passe",
    name: "Nom complet",
    role: "Rôle",
    site: "Site",
    submit: "Continuer",
    mfa_title: "Authentification à deux facteurs",
    mfa_help: "Saisissez le code à 6 chiffres de votre application d'authentification.",
    mfa_setup_title: "Activer la MFA",
    mfa_setup_help: "Scannez le QR code avec Google Authenticator, puis entrez un code.",
    verify: "Vérifier",
    trusted: "Fiable",
    suspicious: "Suspect",
    blocked: "Bloqué",
    allow: "Autoriser",
    deny: "Refuser",
    mfa_plus: "MFA+",
    jit: "JIT",
    real_time_feed: "Flux d'événements temps réel",
    open_alerts: "Alertes ouvertes",
    risk_score: "Score de risque",
    request_access: "Demander l'accès",
    device_posture: "Posture terminal",
    os_updated: "OS à jour",
    antivirus: "Antivirus actif",
    disk_encryption: "Chiffrement disque",
    trusted_network: "Réseau de confiance",
    logs: "Journaux",
    alerts: "Alertes",
    policy_editor: "Éditeur de politiques",
    new_policy: "Nouvelle politique",
    delete: "Supprimer",
    edit: "Modifier",
    save: "Enregistrer",
    cancel: "Annuler",
    run: "Exécuter",
    download_pdf: "Télécharger PDF",
    scenarios_intro: "Rejouez les quatre scénarios de validation du mémoire.",
    simulated_only: "Disponible uniquement en Mode Simulé",
    coverage: "Couverture",
    export_audit: "Exporter l'audit",
  },
};

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("dndi_lang") || "fr");

  useEffect(() => {
    localStorage.setItem("dndi_lang", lang);
  }, [lang]);

  const value = useMemo(
    () => ({
      lang,
      setLang,
      t: (key) => dict[lang]?.[key] || dict.en[key] || key,
    }),
    [lang]
  );
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
